import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import React, { useState, useCallback, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import useTheme from '@mui/styles/useTheme';
import { event } from '../../utils/analytics';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { useSearchApi } from '../../apis';

const useStyles = makeStyles((theme) => ({
	searchRoot: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		padding: theme.spacing(0.25, 1.75),
	},
}));

function SearchInput({ onSearch }) {
	const { t } = useTranslation('search_input');
	const { searchKey } = useParams();
	const theme = useTheme();
	const [searchTerm, setSearchTerm] = useState(searchKey || '');
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const myInput = useRef();
	const [recentlySearchedSuggestions, setRecentlySearchedSuggestions] =
		useState([]);
	const isMountedRef = useIsMountedRef();
	const { getSuggestions } = useSearchApi();

	const getIsLoading = async () => {
		try {
			if (isMountedRef.current) {
				window.localStorage.setItem('isLoading', true);
				window.dispatchEvent(new Event('storage'));

				setTimeout(() => {
					window.localStorage.removeItem('isLoading');
					window.dispatchEvent(new Event('storage'));
				}, 3000);
			}
		} catch (err) {
			console.log('err', err);
		}
	};

	// ----------------------------------------------------------------------------------------------
	/**
	 * Call the API to get the latest search data
	 */
	// ----------------------------------------------------------------------------------------------
	const fetchSuggestions = useCallback(async () => {
		const response = await getSuggestions();
		const { isAborted, isError, error, data } = response;
		if (isAborted) {
			return;
		}

		if (isError) {
			console.error(error?.message || 'get search suggestions failed.');
			return;
		}

		const suggestions = data?.Last_Searched_Values || [];
		setRecentlySearchedSuggestions(suggestions);
	}, [getSuggestions]);

	// ----------------------------------------------------------------------------------------------
	/**
	 * Trigger to fetch the latest search suggestions and open the menu with the fresh data
	 */
	// ----------------------------------------------------------------------------------------------
	const handleClick = useCallback(
		(e) => {
			setAnchorEl(e.currentTarget);
			fetchSuggestions();
		},
		[fetchSuggestions]
	);

	const handleSearch = (e) => {
		e.preventDefault();
		if (onSearch) {
			const trimmedSearch = searchTerm.trim();

			if (trimmedSearch.length > 0) {
				event('event', { action: 'global_search', value: searchTerm });
			}
			handleClose();
			onSearch(trimmedSearch);
		}
	};

	const clearSearch = () => {
		setSearchTerm('');
		onSearch('');
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	function onChange(e) {
		e.preventDefault();
		setSearchTerm(e.target.value);
	}

	// ----------------------------------------------------------------------------------------------
	/**
	 * Trigger a search based on a search term
	 * @param {*} e onClick event from selecting an item in the menu
	 */
	// ----------------------------------------------------------------------------------------------
	const searchByValue = (e) => {
		const selectedSearchTerm = e.target.innerText;
		setSearchTerm(selectedSearchTerm);

		if (selectedSearchTerm.trim().length === 0) {
			return;
		}

		getIsLoading(e);
		handleClose();
		onSearch(selectedSearchTerm.trim());
	};

	return (
		<Box
			role="form"
			id="search-form"
			aria-label="search form"
			style={{
				display: 'flex',
				flexDirection: 'row',
				flex: 1,
			}}
		>
			<form
				aria-label="search form"
				onSubmit={handleSearch}
				style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
				ref={myInput}
			>
				<IconButton
					id={Math.random()}
					aria-label="search icon"
					role="button"
					aria-hidden="false"
					size="small"
					color={theme.palette.gray[50]}
					onClick={handleSearch}
					data-testid="global_search"
				>
					<SearchIcon
						id={Math.random()}
						aria-label="search icon"
						role="graphics-symbol"
						aria-hidden="true"
					/>
				</IconButton>
				<InputBase
					placeholder={t('placeholder')}
					fullWidth
					value={searchTerm}
					onChange={onChange}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && onSearch && searchTerm.trim().length > 0) {
							getIsLoading();
						}
					}}
					style={{
						marginLeft: theme.spacing(2),
						marginRight: theme.spacing(2),
					}}
					onClick={handleClick}
					inputpros={{ 'aria-label': 'search' }}
				/>
				{searchTerm && searchTerm.trim().length > 0 && (
					<IconButton
						id={Math.random()}
						aria-label="search icon"
						role="button"
						aria-hidden="false"
						size="small"
						color={theme.palette.gray.colorGray50}
						onClick={clearSearch}
					>
						<ClearIcon
							id={Math.random()}
							aria-label="clear icon button"
							aria-hidden="true"
							role="graphics-symbol"
						/>
					</IconButton>
				)}

				<Popper
					anchorEl={anchorEl}
					open={open}
					placement="bottom-start"
					style={{ zIndex: 1300 }}
				>
					<ClickAwayListener onClickAway={handleClose}>
						<Paper>
							<MenuList aria-labelledby="basic-button">
								<ListSubheader
									disableSticky
									role="heading"
									aria-level="3"
									tabIndex={0}
								>
									{t('recently_searched')}
								</ListSubheader>
								{recentlySearchedSuggestions.map(
									({ SearchValue: searchValue, props }) => (
										<MenuItem
											aria-label={searchValue}
											tabIndex={0}
											role="list"
											key={searchValue}
											onClick={searchByValue}
											component="a"
											{...props}
										>
											{searchValue}
										</MenuItem>
									)
								)}
							</MenuList>
						</Paper>
					</ClickAwayListener>
				</Popper>
			</form>
		</Box>
	);
}

SearchInput.propTypes = {
	onSearch: PropTypes.func.isRequired,
};

export default SearchInput;
